import {
  createContext,
  useContext,
  ReactNode,
  FC,
  useCallback,
  useState,
  useEffect,
} from "react";
import permissionHandler from "../composables/permission-modules/permissionHandler";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { BasePermissions } from "@/constants/permission-modules/base-permissions";

// Define the context type
interface PermissionContextType {
  isPermitted: (permission: BasePermissions) => boolean;
  isSuperAdmin: boolean;
}

// Create the context
const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined,
);

// Custom hook to use the PermissionContext
export const usePermissionState = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error(
      "usePermissionState must be used within a PermissionStateProvider",
    );
  }
  return context;
};

// Provider component
interface PermissionStateProviderProps {
  children: ReactNode;
}

export const PermissionStateProvider: FC<PermissionStateProviderProps> = ({
  children,
}) => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  /* eslint-disable  react-hooks/exhaustive-deps */
  const { isPermissionGranted } = permissionHandler();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const user: any = useSelector((state: RootState) => state.auth.user);

  const isPermitted = useCallback(
    (permission: BasePermissions) => {
      return user
        ? isPermissionGranted(permission, user.user_type.id, user.permissions)
        : false;
    },
    [user],
  );

  useEffect(() => {
    if (user) {
      setIsSuperAdmin(user.user_type.id === UserTierType.Tier3);
    }
  }, [user]);

  return (
    <PermissionContext.Provider value={{ isPermitted, isSuperAdmin }}>
      {children}
    </PermissionContext.Provider>
  );
};
